import { useSearchParams as useRouterSearchParams } from "react-router-dom";

function useSearchParamHandler() {
  const [searchParams, setSearchParams] = useRouterSearchParams();

  const replaceSearchParam = (paramName, paramValue, oldParams) => {
    const params = new URLSearchParams(oldParams || searchParams); // oldParams is used since Products search has memoized version which loses previous params

    if (params.has(paramName)) {
      params.set(paramName, paramValue);
    } else {
      params.append(paramName, paramValue);
    }

    return setSearchParams(params.toString());
  };

  return replaceSearchParam;
}

/**
 * Centralized domain configuration
 */
const DOMAIN_CONFIG = {
  boxs: {
    name: "Boxs",
    patterns: [
      "app.boxs.in",
      "www.boxs.in",
      "internal.boxs.in",
      "app.boxs.",
      "www.boxs.",
      "internal.boxs.",
    ],
    url: "https://app.boxs.in",
    logoPath: "/store/boxs/logo.png",
    faviconPath: "/favicon.ico",
    description: "Modular Build Platform",
    imageUrl: "https://app.boxs.in/logo.png",
  },
  zerobuild: {
    name: "ZeroBuild",
    patterns: ["zerobuild.", "www.zerobuild.in"],
    url: "https://zerobuild.in",
    logoPath: "/store/logo.png",
    faviconPath: "/store/favicon.ico",
    description: "Modular Cabinet Store",
    imageUrl: "https://zerobuild.in/store/logo.png",
  },
  store: {
    name: "Boxs",
    patterns: ["store."],
    url: "https://store.boxs.in",
    logoPath: "/store/boxs/logo.png",
    faviconPath: "/store/favicon.ico",
    description: "Modular Build Platform",
    imageUrl: "https://store.boxs.in/store/boxs/logo.png",
  },
  other: {
    name: "ZeroBuild",
    patterns: [],
    url: "https://zerobuild.in",
    logoPath: "/store/logo.png",
    faviconPath: "/store/favicon.ico",
    description: "Modular Cabinet Store",
    imageUrl: "https://zerobuild.in/store/logo.png",
  },
};

/**
 * Check if hostname matches any pattern in the given patterns array
 * @param {string} hostname - The hostname to check
 * @param {string[]} patterns - Array of patterns to match against
 * @returns {boolean} True if hostname matches any pattern
 */
const matchesPatterns = (hostname, patterns) => {
  return patterns.some((pattern) =>
    pattern.endsWith(".") ? hostname.startsWith(pattern) : hostname === pattern
  );
};

/**
 * Get the domain type based on the current hostname
 * @returns {string} The domain type - 'zerobuild', 'store', or 'other'
 */
const getDomainType = () => {
  const hostname = window.location.hostname;

  for (const [domainType, config] of Object.entries(DOMAIN_CONFIG)) {
    if (domainType === "other") continue; // Skip 'other' as it's the fallback

    if (matchesPatterns(hostname, config.patterns)) {
      return domainType;
    }
  }

  return "other";
};

/**
 * Get domain configuration for the current domain
 * @returns {Object} Domain configuration object
 */
const getDomainConfig = () => {
  const domainType = getDomainType();
  return DOMAIN_CONFIG[domainType];
};

/**
 * Check if the current environment is the store
 * @returns {boolean} True if on store, false otherwise
 */
export const isStore = () => {
  const domainType = getDomainType();
  return domainType === "zerobuild" || domainType === "store";
};

/** True on zerobuild.in (and local zerobuild.* dev hosts). */
export const isZerobuildDomain = () => getDomainType() === "zerobuild";

/**
 * Get the store brand name based on the current hostname
 * @returns {string} The brand name - 'ZeroBuild' or 'Boxs'
 */
export const getStoreBrandName = () => {
  return getDomainConfig().name;
};

/**
 * Get the store logo path based on the current brand
 * @returns {string} The logo path
 */
export const getStoreLogoPath = () => {
  return getDomainConfig().logoPath;
};

/**
 * Label for a logged-in store user when `name` is unset (falls back to mobile).
 * @param {object | null | undefined} user
 * @returns {string}
 */
export const getStoreUserDisplayName = (user) => {
  const n = user && typeof user.name === "string" ? user.name.trim() : "";
  if (n) return n;
  const m = user?.mobile;
  if (m != null && String(m).trim()) return String(m).trim();
  return "User";
};

/**
 * Reset meta tags to default values when navigating away from a page
 */
export const resetPageMeta = () => {
  setPageMeta(); // This will set the default meta tags based on the current domain
};

/**
 * Get the base path for the current environment
 * @returns {string} The base path - empty string for store, '/store' for main app
 */
export const getBasePath = () => {
  return isStore() ? "" : "/store";
};

/**
 * Set the page title, description, favicon, and Open Graph meta tags based on the current brand
 *
 * This function automatically sets comprehensive meta tags including:
 * - Basic meta tags (title, description)
 * - Open Graph tags (og:title, og:description, og:image, og:url, etc.)
 * - Twitter Card tags (twitter:card, twitter:title, twitter:description, twitter:image)
 * - SEO tags (robots, author, keywords)
 *
 * Usage:
 * - Call this in your main App component or layout component
 * - It will automatically detect the domain and set appropriate tags
 *
 * Example:
 * useEffect(() => {
 *   setPageMeta();
 * }, []);
 */
export const setPageMeta = () => {
  const config = getDomainConfig();
  const { name: title, description, url, imageUrl } = config;
  const domainType = getDomainType();

  document.title = title;

  // Add domain-specific class to body for styling
  document.body.className = document.body.className
    .replace(/\b\w+-domain\b/g, "")
    .trim();
  document.body.classList.add(`${domainType}-domain`);

  // Update or create meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute("content", description);
  } else {
    metaDescription = document.createElement("meta");
    metaDescription.name = "description";
    metaDescription.content = description;
    document.head.appendChild(metaDescription);
  }

  // Open Graph meta tags
  const ogTags = [
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: url },
    { property: "og:image", content: imageUrl },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:site_name", content: title },
    { property: "og:locale", content: "en_US" },
  ];

  // Twitter Card meta tags
  const twitterTags = [
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: imageUrl },
  ];

  // Additional SEO meta tags
  const seoTags = [
    { name: "robots", content: "index, follow" },
    { name: "author", content: title },
    {
      name: "keywords",
      content:
        "modular cabinets, wardrobes, furniture, custom design, home improvement",
    },
  ];

  // Function to update or create meta tags
  const updateMetaTags = (tags) => {
    tags.forEach((tag) => {
      const selector = tag.property
        ? `meta[property="${tag.property}"]`
        : `meta[name="${tag.name}"]`;
      let metaTag = document.querySelector(selector);

      if (metaTag) {
        metaTag.setAttribute("content", tag.content);
      } else {
        metaTag = document.createElement("meta");
        if (tag.property) {
          metaTag.setAttribute("property", tag.property);
        } else {
          metaTag.setAttribute("name", tag.name);
        }
        metaTag.setAttribute("content", tag.content);
        document.head.appendChild(metaTag);
      }
    });
  };

  // Update all meta tags
  updateMetaTags([...ogTags, ...twitterTags, ...seoTags]);

  // Set favicon
  setFavicon();
};

/**
 * Set dynamic meta tags for specific pages (like product pages)
 *
 * This function allows you to set custom meta tags for specific pages,
 * overriding the default meta tags set by setPageMeta().
 *
 * @param {Object} options - Meta tag options
 * @param {string} options.title - Page title (optional)
 * @param {string} options.description - Page description (optional)
 * @param {string} options.imageUrl - Image URL for social sharing (optional)
 * @param {string} options.url - Page URL (optional)
 *
 * Usage:
 * - Call this in your page component when data is loaded
 * - Always call resetPageMeta() in the cleanup function
 *
 * Example:
 * useEffect(() => {
 *   if (product) {
 *     setDynamicPageMeta({
 *       title: `${product.name} - Store Name`,
 *       description: product.description,
 *       imageUrl: product.image,
 *       url: window.location.href
 *     });
 *   }
 *
 *   return () => resetPageMeta();
 * }, [product]);
 */
export const setDynamicPageMeta = (options) => {
  const { title, description, imageUrl, url } = options;

  if (title) {
    document.title = title;
  }

  if (description) {
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", description);
    }
  }

  if (imageUrl || url) {
    const ogTags = [];
    const twitterTags = [];

    if (title) {
      ogTags.push({ property: "og:title", content: title });
      twitterTags.push({ name: "twitter:title", content: title });
    }

    if (description) {
      ogTags.push({ property: "og:description", content: description });
      twitterTags.push({ name: "twitter:description", content: description });
    }

    if (url) {
      ogTags.push({ property: "og:url", content: url });
    }

    if (imageUrl) {
      ogTags.push({ property: "og:image", content: imageUrl });
      twitterTags.push({ name: "twitter:image", content: imageUrl });
    }

    // Update or create meta tags
    const updateMetaTags = (tags) => {
      tags.forEach((tag) => {
        const selector = tag.property
          ? `meta[property="${tag.property}"]`
          : `meta[name="${tag.name}"]`;
        let metaTag = document.querySelector(selector);

        if (metaTag) {
          metaTag.setAttribute("content", tag.content);
        } else {
          metaTag = document.createElement("meta");
          if (tag.property) {
            metaTag.setAttribute("property", tag.property);
          } else {
            metaTag.setAttribute("name", tag.name);
          }
          metaTag.setAttribute("content", tag.content);
          document.head.appendChild(metaTag);
        }
      });
    };

    updateMetaTags([...ogTags, ...twitterTags]);
  }
};

/**
 * Set the favicon based on the current brand
 */
export const setFavicon = () => {
  const faviconPath = getDomainConfig().faviconPath;

  // Update favicon
  let favicon = document.getElementById("favicon-img");
  if (favicon) {
    favicon.href = faviconPath;
  } else {
    favicon = document.createElement("link");
    favicon.rel = "icon";
    favicon.href = faviconPath;
    document.head.appendChild(favicon);
  }
};

/**
 * View Price counter management
 */
const VIEW_PRICE_LIMIT = 2; // Customizable limit - max 3 products

export const getViewPriceCount = () => {
  const count = sessionStorage.getItem("viewPriceCount");
  return count ? parseInt(count) : 0;
};

export const incrementViewPriceCount = () => {
  const currentCount = getViewPriceCount();
  const newCount = currentCount + 1;
  sessionStorage.setItem("viewPriceCount", newCount.toString());
  return newCount;
};

export const canViewPrice = (productId = null) => {
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
  if (currentUser) return true; // Logged in users can always view prices

  const count = getViewPriceCount();
  return count < VIEW_PRICE_LIMIT;
};

export const getViewPriceLimit = () => VIEW_PRICE_LIMIT;

/**
 * Get viewed products from session storage
 */
export const getViewedProducts = () => {
  const stored = sessionStorage.getItem("viewedProducts");
  return stored ? JSON.parse(stored) : [];
};

/**
 * Add product to viewed products list
 */
export const addViewedProduct = (productId) => {
  const viewedProducts = getViewedProducts();

  if (!viewedProducts.includes(productId)) {
    const newViewedProducts = [...viewedProducts, productId];
    sessionStorage.setItem("viewedProducts", JSON.stringify(newViewedProducts));
    return newViewedProducts;
  }
  return viewedProducts;
};

/**
 * Check if a product has been viewed
 */
export const isProductViewed = (productId) => {
  const viewedProducts = getViewedProducts();
  return viewedProducts.includes(productId);
};

// Export domain configuration and utilities for use in other files
export { DOMAIN_CONFIG, getDomainConfig, matchesPatterns };

export default useSearchParamHandler;
